// src/products/dataroom/components/Modals/MergeDataroomModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { X, Merge, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import embedService from '../../api/embedService';
import { supabase } from '../../../../config';

const MergeDataroomModal = ({ isOpen, onClose, currentProjectId, onSuccess, onMessage }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const resetForm = () => {
    setSelectedProjectId('');
    setConfirmMerge(false);
    setError('');
    setShowSuccess(false);
  };

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const availableProjects = await embedService.getEmbeddableProjects(currentProjectId, user.id);
      setProjects(availableProjects);

      if (availableProjects.length === 0) {
        setError('No other datarooms available to merge. You need creator access to merge datarooms.');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load available datarooms: ' + err.message);
    } finally {
      setLoadingProjects(false);
    }
  }, [currentProjectId]);

  useEffect(() => {
    if (isOpen) {
      loadProjects();
      resetForm();
    }
  }, [isOpen, loadProjects]);

  const handleProjectChange = (e) => {
    setSelectedProjectId(e.target.value);
    setConfirmMerge(false);
    setError('');
  };

  const validateForm = () => {
    if (!selectedProjectId) {
      setError('Please select a dataroom to merge');
      return false;
    }
    
    if (!confirmMerge) {
      setError('Please confirm that you understand this action cannot be undone');
      return false;
    }

    return true;
  };

  const handleMerge = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const result = await embedService.mergeDataroom(
        parseInt(selectedProjectId),
        currentProjectId,
        user.id
      );

      // Show success state
      setShowSuccess(true);
      
      // Success feedback
      const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));
      const message = `Successfully merged "${selectedProject?.project_name || 'dataroom'}" - ${result.itemsCopied} items copied`;
      
      // Delay to show success message
      setTimeout(() => {
        onMessage && onMessage(message, 'success');
        onSuccess && onSuccess(result);
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Merge error:', err);
      setError(err.message || 'Failed to merge dataroom');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const selectedProject = projects.find(p => p.id === parseInt(selectedProjectId));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center text-gray-900">
            {showSuccess ? (
              <>
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                Merge Complete
              </>
            ) : (
              <>
                <Merge className="h-5 w-5 mr-2 text-orange-600" />
                Merge Dataroom
              </>
            )}
          </h2>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Success State */}
          {showSuccess && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Merge Completed Successfully!</h3>
              <p className="text-gray-600">
                The dataroom has been merged into your current project. 
                You'll be redirected shortly.
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading && !showSuccess && (
            <div className="text-center py-8">
              <Loader2 className="h-16 w-16 text-orange-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Merging Dataroom...</h3>
              <p className="text-gray-600">
                This may take a few moments depending on the size of the dataroom.
              </p>
            </div>
          )}

          {/* Form State */}
          {!loading && !showSuccess && (
            <>
              {/* Error Display */}
              {error && (
                <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              {/* Loading Projects */}
              {loadingProjects && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-600 mr-2" />
                  <span className="text-gray-600">Loading available datarooms...</span>
                </div>
              )}

              {/* Project Selection */}
              {!loadingProjects && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Dataroom to Merge Into This One
                  </label>
                  <select
                    value={selectedProjectId}
                    onChange={handleProjectChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                    disabled={projects.length === 0}
                  >
                    <option value="">Choose a dataroom...</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.project_name}
                        {project.description && ` - ${project.description.substring(0, 50)}${project.description.length > 50 ? '...' : ''}`}
                      </option>
                    ))}
                  </select>
                  
                  {projects.length === 0 && !loadingProjects && (
                    <p className="text-sm text-gray-500 mt-1">
                      No datarooms available. You need creator access to merge datarooms.
                    </p>
                  )}
                </div>
              )}

              {/* Warning and Confirmation */}
              {selectedProject && !loadingProjects && (
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <div className="flex items-start mb-3">
                    <AlertTriangle className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-orange-800">Important Warning</h4>
                      <div className="text-sm text-orange-700 mt-1 space-y-1">
                        <p>This will permanently copy all folders and files from:</p>
                        <p className="font-medium">"{selectedProject.project_name}"</p>
                        <p>into the current dataroom.</p>
                        <p className="font-semibold text-orange-800">This action cannot be undone.</p>
                      </div>
                    </div>
                  </div>
                  
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmMerge}
                      onChange={(e) => {
                        setConfirmMerge(e.target.checked);
                        setError('');
                      }}
                      className="mt-1 mr-3 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-orange-800 select-none">
                      I understand this action is permanent and cannot be undone. 
                      The original dataroom will remain intact, but all its content 
                      will be copied to this dataroom.
                    </span>
                  </label>
                </div>
              )}

              {/* Additional Info */}
              {selectedProject && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">What happens during merge:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• All folders and files will be copied</li>
                    <li>• Folder structure will be preserved</li>
                    <li>• Content will be placed in a new folder</li>
                    <li>• Original dataroom remains unchanged</li>
                    <li>• Embedded folders will not be copied</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!showSuccess && (
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleMerge}
              disabled={loading || loadingProjects || !selectedProjectId || !confirmMerge || projects.length === 0}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Merging...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  Merge Dataroom
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergeDataroomModal;